
import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

const TermsPage = () => {
  const [activeSection, setActiveSection] = useState('overview');

  const sections = [
    { id: 'overview', title: 'Overview', icon: 'ri-file-text-line' },
    { id: 'acceptance', title: 'Acceptance', icon: 'ri-checkbox-circle-line' },
    { id: 'services', title: 'Services', icon: 'ri-tools-line' },
    { id: 'accounts', title: 'User Accounts', icon: 'ri-user-line' },
    { id: 'payment', title: 'Payment Terms', icon: 'ri-bank-card-line' },
    { id: 'intellectual', title: 'Intellectual Property', icon: 'ri-copyright-line' },
    { id: 'prohibited', title: 'Prohibited Use', icon: 'ri-forbid-line' },
    { id: 'termination', title: 'Termination', icon: 'ri-close-circle-line' },
    { id: 'liability', title: 'Liability', icon: 'ri-shield-line' },
    { id: 'contact', title: 'Contact', icon: 'ri-mail-line' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-600 to-blue-800 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Terms of Service</h1>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              Please read these terms carefully before using our AI-powered real estate platform and services.
            </p>
            <div className="mt-8 text-blue-100">
              <p>Last updated: December 2024</p>
              <p>Effective date: January 1, 2025</p>
            </div>
          </div>
        </div>
      </section>

      {/* Navigation */}
      <section className="bg-gray-50 py-8 sticky top-0 z-10 border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex overflow-x-auto space-x-4 pb-2">
            {sections.map((section) => (
              <button
                key={section.id}
                onClick={() => setActiveSection(section.id)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg whitespace-nowrap transition-colors ${
                  activeSection === section.id
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-100'
                }`}
              >
                <i className={`${section.icon} text-lg`}></i>
                <span>{section.title}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Overview */}
          {activeSection === 'overview' && (
            <div className="prose prose-lg max-w-none">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Terms of Service Overview</h2>
              <p className="text-gray-600 mb-6">
                Welcome to AI Consulting Authority. These Terms of Service ("Terms") govern your use of our AI-powered real estate platform, tools, and services. By accessing or using our services, you agree to be bound by these Terms.
              </p>
              
              <div className="bg-blue-50 p-6 rounded-lg mb-8">
                <h3 className="text-xl font-semibold text-blue-900 mb-4">Quick Summary</h3>
                <ul className="space-y-2 text-blue-800">
                  <li>• You must be a licensed real estate professional to use our services</li>
                  <li>• Our AI tools are designed to enhance, not replace, your professional judgment</li>
                  <li>• You retain ownership of your data and client information</li>
                  <li>• We provide the platform "as is" with industry-standard warranties</li>
                  <li>• Either party may terminate the agreement with proper notice</li>
                </ul>
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                <div className="bg-white p-6 rounded-lg border">
                  <h4 className="text-lg font-semibold text-gray-900 mb-3">Your Responsibilities</h4>
                  <ul className="text-gray-600 space-y-1">
                    <li>• Maintain valid real estate license</li>
                    <li>• Use services professionally and ethically</li>
                    <li>• Protect your account credentials</li>
                    <li>• Comply with applicable laws</li>
                  </ul>
                </div>
                <div className="bg-white p-6 rounded-lg border">
                  <h4 className="text-lg font-semibold text-gray-900 mb-3">Our Commitments</h4>
                  <ul className="text-gray-600 space-y-1">
                    <li>• Provide reliable AI services</li>
                    <li>• Protect your data and privacy</li>
                    <li>• Offer customer support</li>
                    <li>• Maintain platform security</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Acceptance */}
          {activeSection === 'acceptance' && (
            <div className="prose prose-lg max-w-none">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Acceptance of Terms</h2>
              
              <div className="space-y-6">
                <div className="bg-green-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-green-900 mb-4">Agreement to Terms</h3>
                  <p className="text-green-800 mb-4">
                    By accessing, browsing, or using our platform, you acknowledge that you have read, understood, and agree to be bound by these Terms and our Privacy Policy.
                  </p>
                  <ul className="text-green-800 space-y-2">
                    <li>• Creating an account constitutes acceptance</li>
                    <li>• Continued use after updates constitutes acceptance of new terms</li>
                    <li>• You must be 18 years or older to use our services</li>
                    <li>• You must have legal authority to enter into this agreement</li>
                  </ul>
                </div>

                <div className="bg-white p-6 rounded-lg border">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Professional Requirements</h3>
                  <p className="text-gray-600 mb-4">Our services are designed for licensed real estate professionals. You represent and warrant that:</p>
                  <ul className="text-gray-600 space-y-2">
                    <li>• You hold a valid real estate license in your jurisdiction</li>
                    <li>• You are in good standing with your licensing authority</li>
                    <li>• You will use our services in compliance with real estate laws and regulations</li>
                    <li>• You will maintain professional standards and ethics</li>
                  </ul>
                </div>

                <div className="bg-yellow-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-yellow-900 mb-4">Updates to Terms</h3>
                  <p className="text-yellow-800">
                    We may update these Terms from time to time. We will notify you of material changes via email or platform notification. Your continued use after such notice constitutes acceptance of the updated Terms.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Services */}
          {activeSection === 'services' && (
            <div className="prose prose-lg max-w-none">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Description of Services</h2>
              
              <div className="space-y-6">
                <div className="bg-blue-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-blue-900 mb-4">AI-Powered Real Estate Tools</h3>
                  <p className="text-blue-800 mb-4">
                    We provide artificial intelligence tools designed to enhance real estate professionals' productivity and effectiveness:
                  </p>
                  <div className="grid md:grid-cols-2 gap-4 text-blue-800">
                    <ul className="space-y-2">
                      <li>• Lead generation and qualification</li>
                      <li>• Automated listing creation</li>
                      <li>• Market analytics and insights</li>
                      <li>• Client relationship management</li>
                    </ul>
                    <ul className="space-y-2">
                      <li>• AI chatbots and virtual assistants</li>
                      <li>• Social media content generation</li>
                      <li>• Workflow automation</li>
                      <li>• Predictive analytics</li>
                    </ul>
                  </div>
                </div>

                <div className="bg-white p-6 rounded-lg border">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Service Availability</h3>
                  <div className="space-y-4">
                    <div className="flex items-start space-x-3">
                      <i className="ri-time-line text-blue-600 text-xl mt-1"></i>
                      <div>
                        <h4 className="font-semibold text-gray-900">Uptime Commitment</h4>
                        <p className="text-gray-600">We strive for 99.9% uptime with scheduled maintenance windows.</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <i className="ri-refresh-line text-blue-600 text-xl mt-1"></i>
                      <div>
                        <h4 className="font-semibold text-gray-900">Updates and Improvements</h4>
                        <p className="text-gray-600">Regular updates to improve functionality and add new features.</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <i className="ri-customer-service-line text-blue-600 text-xl mt-1"></i>
                      <div>
                        <h4 className="font-semibold text-gray-900">Support</h4>
                        <p className="text-gray-600">Customer support during business hours with emergency support available.</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-orange-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-orange-900 mb-4">Service Limitations</h3>
                  <ul className="text-orange-800 space-y-2">
                    <li>• AI tools provide suggestions and automation, not professional advice</li>
                    <li>• You remain responsible for all professional decisions and client interactions</li>
                    <li>• Services may have usage limits based on your subscription plan</li>
                    <li>• Some features may require third-party integrations</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* User Accounts */}
          {activeSection === 'accounts' && (
            <div className="prose prose-lg max-w-none">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">User Accounts and Responsibilities</h2>
              
              <div className="space-y-6">
                <div className="bg-white p-6 rounded-lg border">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Account Creation</h3>
                  <p className="text-gray-600 mb-4">To use our services, you must create an account by providing:</p>
                  <ul className="text-gray-600 space-y-2">
                    <li>• Accurate and complete registration information</li>
                    <li>• Valid email address and contact information</li>
                    <li>• Real estate license number and verification</li>
                    <li>• Brokerage affiliation details</li>
                  </ul>
                </div>

                <div className="bg-red-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-red-900 mb-4">Account Security</h3>
                  <p className="text-red-800 mb-4">You are responsible for maintaining the security of your account:</p>
                  <ul className="text-red-800 space-y-2">
                    <li>• Keep your password confidential and secure</li>
                    <li>• Enable two-factor authentication when available</li>
                    <li>• Notify us immediately of any unauthorized access</li>
                    <li>• Log out from shared or public computers</li>
                    <li>• Regularly update your account information</li>
                  </ul>
                </div>

                <div className="bg-blue-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-blue-900 mb-4">Account Usage</h3>
                  <div className="space-y-4 text-blue-800">
                    <div>
                      <h4 className="font-semibold">Personal Use Only</h4>
                      <p>Your account is for your personal professional use and may not be shared or transferred.</p>
                    </div>
                    <div>
                      <h4 className="font-semibold">Accurate Information</h4>
                      <p>You must keep your account information current and accurate at all times.</p>
                    </div>
                    <div>
                      <h4 className="font-semibold">Professional Standards</h4>
                      <p>Use the platform in accordance with real estate professional standards and ethics.</p>
                    </div>
                  </div>
                </div>

                <div className="bg-yellow-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-yellow-900 mb-4">Account Suspension</h3>
                  <p className="text-yellow-800 mb-4">We may suspend or terminate accounts for:</p>
                  <ul className="text-yellow-800 space-y-2">
                    <li>• Violation of these Terms or our policies</li>
                    <li>• Fraudulent or illegal activity</li>
                    <li>• Non-payment of fees</li>
                    <li>• Abuse of our services or other users</li>
                    <li>• Loss of real estate license</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Payment Terms */}
          {activeSection === 'payment' && (
            <div className="prose prose-lg max-w-none">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Payment Terms and Billing</h2>
              
              <div className="space-y-6">
                <div className="bg-green-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-green-900 mb-4">Subscription Plans</h3>
                  <p className="text-green-800 mb-4">We offer various subscription plans with different features and usage limits:</p>
                  <div className="grid md:grid-cols-3 gap-4 text-green-800">
                    <div>
                      <h4 className="font-semibold">Starter Plan</h4>
                      <p>$99/month - Basic AI tools</p>
                    </div>
                    <div>
                      <h4 className="font-semibold">Professional Plan</h4>
                      <p>$299/month - Advanced features</p>
                    </div>
                    <div>
                      <h4 className="font-semibold">Enterprise Plan</h4>
                      <p>Custom pricing - Full suite</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-6 rounded-lg border">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Billing and Payment</h3>
                  <div className="space-y-4">
                    <div className="flex items-start space-x-3">
                      <i className="ri-calendar-line text-blue-600 text-xl mt-1"></i>
                      <div>
                        <h4 className="font-semibold text-gray-900">Billing Cycle</h4>
                        <p className="text-gray-600">Monthly or annual billing available. Annual plans receive a discount.</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <i className="ri-bank-card-line text-blue-600 text-xl mt-1"></i>
                      <div>
                        <h4 className="font-semibold text-gray-900">Payment Methods</h4>
                        <p className="text-gray-600">Credit cards, ACH transfers, and wire transfers accepted.</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <i className="ri-refresh-line text-blue-600 text-xl mt-1"></i>
                      <div>
                        <h4 className="font-semibold text-gray-900">Auto-Renewal</h4>
                        <p className="text-gray-600">Subscriptions automatically renew unless cancelled before the next billing cycle.</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-blue-900 mb-4">Refund Policy</h3>
                  <ul className="text-blue-800 space-y-2">
                    <li>• 30-day money-back guarantee for new subscribers</li>
                    <li>• Pro-rated refunds for annual plans cancelled within 60 days</li>
                    <li>• No refunds for partial months or usage-based fees</li>
                    <li>• Refunds processed within 5-10 business days</li>
                  </ul>
                </div>

                <div className="bg-red-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-red-900 mb-4">Late Payments and Suspension</h3>
                  <ul className="text-red-800 space-y-2">
                    <li>• Accounts may be suspended for non-payment after 7 days</li>
                    <li>• Late fees may apply to overdue accounts</li>
                    <li>• Service restoration upon payment of all outstanding amounts</li>
                    <li>• Data retention for 30 days after suspension</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Intellectual Property */}
          {activeSection === 'intellectual' && (
            <div className="prose prose-lg max-w-none">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Intellectual Property Rights</h2>
              
              <div className="space-y-6">
                <div className="bg-blue-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-blue-900 mb-4">Our Intellectual Property</h3>
                  <p className="text-blue-800 mb-4">
                    AI Consulting Authority owns all rights, title, and interest in our platform, including:
                  </p>
                  <ul className="text-blue-800 space-y-2">
                    <li>• Software, algorithms, and AI models</li>
                    <li>• User interface and design elements</li>
                    <li>• Trademarks, logos, and brand materials</li>
                    <li>• Documentation and training materials</li>
                    <li>• Proprietary data and analytics</li>
                  </ul>
                </div>

                <div className="bg-green-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-green-900 mb-4">Your Data and Content</h3>
                  <p className="text-green-800 mb-4">You retain ownership of your data and content, including:</p>
                  <ul className="text-green-800 space-y-2">
                    <li>• Client information and contact data</li>
                    <li>• Property listings and descriptions</li>
                    <li>• Marketing materials you create</li>
                    <li>• Business data and analytics</li>
                  </ul>
                  <p className="text-green-800 mt-4">
                    You grant us a limited license to use your data solely to provide our services to you.
                  </p>
                </div>

                <div className="bg-white p-6 rounded-lg border">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">License to Use Our Services</h3>
                  <p className="text-gray-600 mb-4">
                    We grant you a limited, non-exclusive, non-transferable license to use our platform for your professional real estate activities, subject to these Terms.
                  </p>
                  <div className="space-y-3">
                    <div>
                      <h4 className="font-semibold text-gray-900">Permitted Uses:</h4>
                      <ul className="text-gray-600 space-y-1">
                        <li>• Generate leads and manage clients</li>
                        <li>• Create marketing materials and listings</li>
                        <li>• Analyze market data and trends</li>
                        <li>• Automate routine business tasks</li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">Prohibited Uses:</h4>
                      <ul className="text-gray-600 space-y-1">
                        <li>• Reverse engineer or copy our technology</li>
                        <li>• Resell or redistribute our services</li>
                        <li>• Use for illegal or unethical purposes</li>
                        <li>• Interfere with platform security or performance</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="bg-purple-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-purple-900 mb-4">DMCA and Copyright Protection</h3>
                  <p className="text-purple-800 mb-4">
                    We respect intellectual property rights and comply with the Digital Millennium Copyright Act (DMCA). If you believe your copyrighted work has been infringed, please contact our DMCA agent.
                  </p>
                  <div className="text-purple-800">
                    <p><strong>DMCA Agent:</strong> legal@aiconsultingauthority.com</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Prohibited Use */}
          {activeSection === 'prohibited' && (
            <div className="prose prose-lg max-w-none">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Prohibited Uses and Conduct</h2>
              
              <div className="space-y-6">
                <div className="bg-red-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-red-900 mb-4">Strictly Prohibited Activities</h3>
                  <p className="text-red-800 mb-4">You may not use our services for any of the following:</p>
                  <div className="grid md:grid-cols-2 gap-4 text-red-800">
                    <ul className="space-y-2">
                      <li>• Illegal activities or fraud</li>
                      <li>• Harassment or discrimination</li>
                      <li>• Spam or unsolicited communications</li>
                      <li>• Malware or security threats</li>
                      <li>• Unauthorized access attempts</li>
                    </ul>
                    <ul className="space-y-2">
                      <li>• Data scraping or harvesting</li>
                      <li>• Reverse engineering our platform</li>
                      <li>• Violating third-party rights</li>
                      <li>• Circumventing usage limits</li>
                      <li>• Creating fake accounts</li>
                    </ul>
                  </div>
                </div>

                <div className="bg-yellow-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-yellow-900 mb-4">Professional Standards</h3>
                  <p className="text-yellow-800 mb-4">As a real estate professional, you must:</p>
                  <ul className="text-yellow-800 space-y-2">
                    <li>• Comply with all applicable real estate laws and regulations</li>
                    <li>• Follow your local MLS rules and guidelines</li>
                    <li>• Maintain client confidentiality and privacy</li>
                    <li>• Provide accurate and truthful information</li>
                    <li>• Respect fair housing laws and anti-discrimination policies</li>
                  </ul>
                </div>

                <div className="bg-white p-6 rounded-lg border">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Content Guidelines</h3>
                  <p className="text-gray-600 mb-4">When using our platform to create content, ensure it is:</p>
                  <div className="space-y-4">
                    <div className="flex items-start space-x-3">
                      <i className="ri-check-line text-green-600 text-xl mt-1"></i>
                      <div>
                        <h4 className="font-semibold text-gray-900">Acceptable Content</h4>
                        <ul className="text-gray-600 space-y-1">
                          <li>• Professional and appropriate</li>
                          <li>• Accurate and truthful</li>
                          <li>• Compliant with advertising laws</li>
                          <li>• Respectful of all individuals</li>
                        </ul>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <i className="ri-close-line text-red-600 text-xl mt-1"></i>
                      <div>
                        <h4 className="font-semibold text-gray-900">Prohibited Content</h4>
                        <ul className="text-gray-600 space-y-1">
                          <li>• Misleading or false information</li>
                          <li>• Discriminatory language or imagery</li>
                          <li>• Copyrighted material without permission</li>
                          <li>• Offensive or inappropriate content</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-orange-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-orange-900 mb-4">Enforcement and Consequences</h3>
                  <p className="text-orange-800 mb-4">Violations of these prohibited uses may result in:</p>
                  <ul className="text-orange-800 space-y-2">
                    <li>• Warning and required corrective action</li>
                    <li>• Temporary suspension of services</li>
                    <li>• Permanent account termination</li>
                    <li>• Legal action for serious violations</li>
                    <li>• Reporting to relevant authorities</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Termination */}
          {activeSection === 'termination' && (
            <div className="prose prose-lg max-w-none">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Termination of Services</h2>
              
              <div className="space-y-6">
                <div className="bg-blue-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-blue-900 mb-4">Termination by You</h3>
                  <p className="text-blue-800 mb-4">You may terminate your account at any time:</p>
                  <ul className="text-blue-800 space-y-2">
                    <li>• Cancel through your account settings</li>
                    <li>• Contact customer support for assistance</li>
                    <li>• 30-day notice required for annual plans</li>
                    <li>• Immediate termination available for monthly plans</li>
                  </ul>
                </div>

                <div className="bg-red-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-red-900 mb-4">Termination by Us</h3>
                  <p className="text-red-800 mb-4">We may terminate your account for:</p>
                  <ul className="text-red-800 space-y-2">
                    <li>• Violation of these Terms of Service</li>
                    <li>• Non-payment of fees after notice</li>
                    <li>• Fraudulent or illegal activity</li>
                    <li>• Abuse of our services or other users</li>
                    <li>• Loss of required professional licensing</li>
                  </ul>
                </div>

                <div className="bg-white p-6 rounded-lg border">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Effect of Termination</h3>
                  <p className="text-gray-600 mb-4">Upon termination of your account:</p>
                  <div className="space-y-4">
                    <div className="flex items-start space-x-3">
                      <i className="ri-time-line text-blue-600 text-xl mt-1"></i>
                      <div>
                        <h4 className="font-semibold text-gray-900">Immediate Effects</h4>
                        <ul className="text-gray-600 space-y-1">
                          <li>• Access to services is suspended</li>
                          <li>• Automated processes are stopped</li>
                          <li>• New charges cease immediately</li>
                        </ul>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <i className="ri-download-line text-blue-600 text-xl mt-1"></i>
                      <div>
                        <h4 className="font-semibold text-gray-900">Data Export</h4>
                        <ul className="text-gray-600 space-y-1">
                          <li>• 30 days to export your data</li>
                          <li>• Self-service export tools available</li>
                          <li>• Support assistance if needed</li>
                        </ul>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <i className="ri-delete-bin-line text-blue-600 text-xl mt-1"></i>
                      <div>
                        <h4 className="font-semibold text-gray-900">Data Deletion</h4>
                        <ul className="text-gray-600 space-y-1">
                          <li>• Data deleted after 30 days</li>
                          <li>• Backup retention per privacy policy</li>
                          <li>• Legal hold exceptions may apply</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-green-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-green-900 mb-4">Survival of Terms</h3>
                  <p className="text-green-800 mb-4">The following provisions survive termination:</p>
                  <ul className="text-green-800 space-y-2">
                    <li>• Payment obligations for services used</li>
                    <li>• Intellectual property rights</li>
                    <li>• Limitation of liability clauses</li>
                    <li>• Dispute resolution procedures</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Liability */}
          {activeSection === 'liability' && (
            <div className="prose prose-lg max-w-none">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Limitation of Liability and Warranties</h2>
              
              <div className="space-y-6">
                <div className="bg-yellow-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-yellow-900 mb-4">Service Warranties</h3>
                  <p className="text-yellow-800 mb-4">We provide our services with the following warranties:</p>
                  <ul className="text-yellow-800 space-y-2">
                    <li>• Services will perform substantially as described</li>
                    <li>• We will use commercially reasonable efforts to maintain uptime</li>
                    <li>• We will protect your data with industry-standard security</li>
                    <li>• We will provide customer support as outlined in your plan</li>
                  </ul>
                </div>

                <div className="bg-red-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-red-900 mb-4">Disclaimer of Warranties</h3>
                  <p className="text-red-800 mb-4">
                    EXCEPT AS EXPRESSLY STATED, OUR SERVICES ARE PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND, INCLUDING:
                  </p>
                  <ul className="text-red-800 space-y-2">
                    <li>• Implied warranties of merchantability or fitness</li>
                    <li>• Guarantees of uninterrupted or error-free operation</li>
                    <li>• Warranties regarding third-party integrations</li>
                    <li>• Guarantees of specific business results or outcomes</li>
                  </ul>
                </div>

                <div className="bg-white p-6 rounded-lg border">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Limitation of Liability</h3>
                  <p className="text-gray-600 mb-4">
                    Our liability to you is limited as follows:
                  </p>
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-semibold text-gray-900">Maximum Liability</h4>
                      <p className="text-gray-600">
                        Our total liability shall not exceed the amount you paid us in the 12 months preceding the claim.
                      </p>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">Excluded Damages</h4>
                      <p className="text-gray-600">
                        We are not liable for indirect, incidental, special, consequential, or punitive damages, including lost profits or business opportunities.
                      </p>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">Exceptions</h4>
                      <p className="text-gray-600">
                        These limitations do not apply to our gross negligence, willful misconduct, or violations of applicable law.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-blue-900 mb-4">Your Responsibilities</h3>
                  <p className="text-blue-800 mb-4">You acknowledge and agree that:</p>
                  <ul className="text-blue-800 space-y-2">
                    <li>• You are responsible for your professional decisions and client relationships</li>
                    <li>• Our AI tools provide suggestions, not professional advice</li>
                    <li>• You must verify all information before using it professionally</li>
                    <li>• You will maintain appropriate professional insurance</li>
                    <li>• You will comply with all applicable laws and regulations</li>
                  </ul>
                </div>

                <div className="bg-purple-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-purple-900 mb-4">Indemnification</h3>
                  <p className="text-purple-800 mb-4">
                    You agree to indemnify and hold us harmless from claims arising from:
                  </p>
                  <ul className="text-purple-800 space-y-2">
                    <li>• Your use of our services</li>
                    <li>• Your violation of these Terms</li>
                    <li>• Your violation of applicable laws</li>
                    <li>• Your professional activities and client relationships</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Contact */}
          {activeSection === 'contact' && (
            <div className="prose prose-lg max-w-none">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Contact Information and Legal</h2>
              
              <div className="space-y-6">
                <div className="bg-blue-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-blue-900 mb-4">Questions About These Terms?</h3>
                  <p className="text-blue-800">
                    If you have any questions about these Terms of Service, please contact our legal team.
                  </p>
                </div>

                <div className="grid md:grid-cols-2 gap-8">
                  <div className="space-y-6">
                    <div className="flex items-start space-x-4">
                      <i className="ri-mail-line text-blue-600 text-2xl mt-1"></i>
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900">Legal Department</h4>
                        <p className="text-gray-600">legal@aiconsultingauthority.com</p>
                        <p className="text-sm text-gray-500">Response within 48 hours</p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-4">
                      <i className="ri-phone-line text-blue-600 text-2xl mt-1"></i>
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900">Legal Hotline</h4>
                        <p className="text-gray-600">1-800-AI-LEGAL</p>
                        <p className="text-sm text-gray-500">Mon-Fri, 9 AM - 5 PM EST</p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-4">
                      <i className="ri-map-pin-line text-blue-600 text-2xl mt-1"></i>
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900">Mailing Address</h4>
                        <p className="text-gray-600">
                          AI Consulting Authority<br />
                          Legal Department<br />
                          123 Innovation Drive<br />
                          San Francisco, CA 94105
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-6">
                    <div className="bg-white p-6 rounded-lg border">
                      <h4 className="text-lg font-semibold text-gray-900 mb-4">Governing Law</h4>
                      <p className="text-gray-600 mb-3">
                        These Terms are governed by the laws of the State of California, without regard to conflict of law principles.
                      </p>
                      <p className="text-gray-600">
                        Any disputes will be resolved in the state or federal courts located in San Francisco County, California.
                      </p>
                    </div>

                    <div className="bg-white p-6 rounded-lg border">
                      <h4 className="text-lg font-semibold text-gray-900 mb-4">Dispute Resolution</h4>
                      <p className="text-gray-600 mb-3">
                        We encourage resolving disputes through direct communication first.
                      </p>
                      <p className="text-gray-600">
                        For formal disputes, we offer mediation and arbitration options before litigation.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h4 className="text-lg font-semibold text-gray-900 mb-3">Entire Agreement</h4>
                  <p className="text-gray-600 mb-3">
                    These Terms, together with our Privacy Policy and any additional terms for specific services, constitute the entire agreement between you and AI Consulting Authority.
                  </p>
                  <p className="text-gray-600">
                    If any provision of these Terms is found to be unenforceable, the remaining provisions will remain in full force and effect.
                  </p>
                </div>
              </div>
            </div>
          )}

        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TermsPage;
